/**
 * FO-058 (batch-18) Deel B — pure helpers voor de geautomatiseerde
 * placeholder-mails. De feitelijke app-query-resolvers (DB) leven in
 * `apps/api/src/services/mail-automatisering` (spec: resolver-register onder
 * apps/api, niet in packages/utils); hier alleen zuivere, herbruikbare logica:
 * nette opsomming, doel-datum-berekening en placeholder-substitutie.
 *
 * FO-148 (batch-29) — aangevuld met de pure dekkingsmail-helpers (doelweek,
 * datumbereik, samenvatting en rapport-rendering) onderaan dit bestand.
 */

import { mondayOfIsoWeek, parseWeekString, weekStringFromDate } from './iso-week'
import type { OnderhoudDagResultaat } from './onderhoud-controle'

const DAG_MS = 86_400_000
const PLACEHOLDER_RE = /\{\{[a-zA-Z0-9_.]+\}\}/g

/**
 * Nette Nederlandse opsomming: [] -> "", ["Jan"] -> "Jan",
 * ["Jan","Piet"] -> "Jan en Piet", ["Jan","Piet","Klaas"] -> "Jan, Piet en Klaas".
 */
export function formatNederlandseLijst(items: string[]): string {
  const schoon = items.map((s) => s.trim()).filter((s) => s.length > 0)
  if (schoon.length === 0) return ''
  if (schoon.length === 1) return schoon[0]!
  return `${schoon.slice(0, -1).join(', ')} en ${schoon[schoon.length - 1]!}`
}

/**
 * UTC-datum van een [weekdag] in week +N t.o.v. de referentie-maandag.
 * `refMaandag` is de UTC-maandag van de huidige ISO-week (week start maandag);
 * `weekdag` is 1-7 (ma=1 .. zo=7); `weekOffset` is het aantal weken vooruit
 * (0 = deze week, 1 = volgende week, ...).
 */
export function datumVanWeekdagWeek(refMaandag: Date, weekdag: number, weekOffset: number): Date {
  const doelMaandag = refMaandag.getTime() + weekOffset * 7 * DAG_MS
  return new Date(doelMaandag + (weekdag - 1) * DAG_MS)
}

/**
 * Vervangt {{token}}-placeholders door hun waarde. `waarden` is gesleuteld op de
 * token-NAAM zonder accolades (bv. "geplande_monteurs"). Een token zonder waarde
 * wordt vervangen door een lege string (geen achtergebleven `{{...}}` in de mail);
 * de orchestrator levert voor elk geconfigureerd token een waarde en signaleert
 * ontbrekende verplichte data apart.
 */
export function vulPlaceholders(text: string, waarden: Record<string, string>): string {
  return text.replace(PLACEHOLDER_RE, (match) => {
    const naam = match.slice(2, -2)
    return Object.prototype.hasOwnProperty.call(waarden, naam) ? waarden[naam]! : ''
  })
}

/** Unieke token-namen (zonder accolades) in een tekst, in volgorde van voorkomen. */
export function extractPlaceholderNamen(text: string): string[] {
  const matches = text.match(PLACEHOLDER_RE)
  if (!matches) return []
  const namen = matches.map((m) => m.slice(2, -2))
  return Array.from(new Set(namen))
}

// ═════════════════════════════════════════════════════════════════════════════
// FO-148 (batch-29) — pure helpers voor de wekelijkse dekkingsmail.
//
// De resolver (`dekking_volgende_week` in apps/api) haalt de data op via de
// gedeelde dekking-service + onderhoud-evaluatie; hier staat uitsluitend de
// zuivere logica: doelweek-afleiding (ISO-week +1, jaargrens-veilig),
// NL-datumbereik, totalen, de onderwerp-samenvatting en de tekst-rendering.
// Alles UTC-kalenderdagen (weekgrenzen zijn UTC-maandagen, zoals overal in de
// dekking-keten); geen persoonsgegevens of tarieven, alleen klantnaam/aantallen.
// ═════════════════════════════════════════════════════════════════════════════

/** Doelweek van het dekkingsrapport (ISO-week +N t.o.v. de referentieweek). */
export interface DekkingDoelweek {
  /** UTC-maandag (00:00) van de doelweek. */
  maandag: Date
  /** UTC-zondag (00:00) van de doelweek (maandag + 6 dagen). */
  zondag: Date
  /** ISO-weeknummer van de doelweek (1-53). */
  weekNummer: number
  /** ISO-weekjaar van de doelweek (kan afwijken van het kalenderjaar). */
  jaar: number
  /** yyyy-mm-dd van de maandag (voor de deeplink `?van=`). */
  maandagIso: string
  /** yyyy-mm-dd van de zondag (voor de deeplink `&tot=`). */
  zondagIso: string
}

/**
 * Bepaalt de doelweek voor het dekkingsrapport: de ISO-week `weekOffset` weken
 * ná de week van `refMaandag` (default +1 = volgende week, FO-148).
 *
 * `refMaandag` is de UTC-maandag van de referentieweek (zoals de
 * mail-automatisering-engine die aanlevert). De input wordt defensief naar de
 * ISO-maandag genormaliseerd; optellen gebeurt in hele UTC-dagen, dus DST-
 * overgangen (maart/oktober) en de jaargrens (52/53-wekenjaren) zijn veilig —
 * het ISO-weeknummer/weekjaar komt uit `weekStringFromDate`.
 */
export function doelweekVoorDekkingsmail(refMaandag: Date, weekOffset = 1): DekkingDoelweek {
  const ref = parseWeekString(weekStringFromDate(refMaandag))
  const basisMaandag = mondayOfIsoWeek(ref.week, ref.year)
  const maandag = new Date(basisMaandag.getTime() + weekOffset * 7 * DAG_MS)
  const zondag = new Date(maandag.getTime() + 6 * DAG_MS)
  const doel = parseWeekString(weekStringFromDate(maandag))
  return {
    maandag,
    zondag,
    weekNummer: doel.week,
    jaar: doel.year,
    maandagIso: maandag.toISOString().slice(0, 10),
    zondagIso: zondag.toISOString().slice(0, 10),
  }
}

const NL_DAG_MAAND = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
})
const NL_DAG_MAAND_JAAR = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

/**
 * NL-datumbereik van een doelweek: "13 t/m 19 juli 2026",
 * over een maandgrens "29 juni t/m 5 juli 2026",
 * over een jaargrens "29 december 2025 t/m 4 januari 2026".
 * Inputs zijn UTC-kalenderdagen (weekgrenzen), dus formattering in UTC.
 */
export function formatDekkingDatumBereik(maandag: Date, zondag: Date): string {
  if (maandag.getUTCFullYear() !== zondag.getUTCFullYear()) {
    return `${NL_DAG_MAAND_JAAR.format(maandag)} t/m ${NL_DAG_MAAND_JAAR.format(zondag)}`
  }
  if (maandag.getUTCMonth() === zondag.getUTCMonth()) {
    return `${maandag.getUTCDate()} t/m ${NL_DAG_MAAND_JAAR.format(zondag)}`
  }
  return `${NL_DAG_MAAND.format(maandag)} t/m ${NL_DAG_MAAND_JAAR.format(zondag)}`
}

/** Eén klantrooster-rij met een dekkingsafwijking (TE_WEINIG of TE_VEEL). */
export interface DekkingRoosterRegel {
  clientId: string
  clientNaam: string
  roosterNaam: string
  verwacht: number
  /** FO-348 — gedekte dienst-slots (altijd <= verwacht). */
  gepland: number
  /** FO-348 — dagen met werk buiten elk roosterslot; de bron van een TE_VEEL. */
  extraDagen: number
}

/** Eén klant met een onderhoudscontrole-afwijking in de doelweek. */
export interface DekkingOnderhoudRegel {
  clientId: string
  clientNaam: string
  /** Label van de afwijking ('niet gepland' / 'onvolledig gepland' / 'onjuist gepland'). */
  statusLabel: string
  /** Per-dag-opsomming (zie {@link bouwOnderhoudDagenDetail}). */
  detail: string
  /** Ontbrekende monteur-dagen (voor het totaal in de kopregel). */
  ontbrekendeDiensten: number
}

/**
 * Korte NL-opsomming van de afwijkende dagen van een onderhoud-evaluatie.
 * Zelfde rendering als de onderhoudscontrole-melding (FO-058), zodat mail en
 * Postvak-melding dezelfde taal spreken.
 */
export function bouwOnderhoudDagenDetail(dagen: OnderhoudDagResultaat[]): string {
  const WEEKDAG_NAMEN = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag']
  const delen: string[] = []
  for (const d of dagen) {
    const naam = WEEKDAG_NAMEN[d.weekdag - 1] ?? `dag ${d.weekdag}`
    if (d.status === 'ONTBREEKT') delen.push(`${naam}: niemand gepland (${d.verwacht} verwacht)`)
    else if (d.status === 'TE_WEINIG') delen.push(`${naam}: ${d.gepland} van ${d.verwacht} monteurs`)
    else if (d.status === 'TE_VEEL') delen.push(`${naam}: ${d.gepland} gepland, ${d.verwacht} verwacht`)
    else if (d.status === 'ONVERWACHT') delen.push(`${naam}: ${d.gepland} gepland (niet in het schema)`)
  }
  return delen.length > 0 ? delen.join('; ') : 'de planning voldoet niet aan het onderhoudsschema.'
}

/** Ontbrekende monteur-dagen in een onderhoud-evaluatie: som van max(0, verwacht - gepland). */
export function telOnderhoudOntbrekendeDiensten(dagen: OnderhoudDagResultaat[]): number {
  let som = 0
  for (const d of dagen) som += Math.max(0, d.verwacht - d.gepland)
  return som
}

export interface DekkingTotalen {
  /** Unieke klanten met een tekort (rooster-TE_WEINIG en/of onderhoud-afwijking). */
  klantenMetTekort: number
  /** Ontbrekende diensten: rooster (verwacht - gepland) + onderhoud (monteur-dagen). */
  ontbrekendeDiensten: number
  /** Unieke klanten met rooster-overbezetting (TE_VEEL). */
  klantenTeVeel: number
}

export function telDekkingTotalen(
  roosterTekorten: DekkingRoosterRegel[],
  onderhoudAfwijkingen: DekkingOnderhoudRegel[],
  roosterTeVeel: DekkingRoosterRegel[],
): DekkingTotalen {
  const tekortKlanten = new Set<string>()
  let ontbrekend = 0
  for (const r of roosterTekorten) {
    tekortKlanten.add(r.clientId)
    ontbrekend += Math.max(0, r.verwacht - r.gepland)
  }
  for (const o of onderhoudAfwijkingen) {
    tekortKlanten.add(o.clientId)
    ontbrekend += Math.max(0, o.ontbrekendeDiensten)
  }
  const teVeelKlanten = new Set(roosterTeVeel.map((r) => r.clientId))
  return {
    klantenMetTekort: tekortKlanten.size,
    ontbrekendeDiensten: ontbrekend,
    klantenTeVeel: teVeelKlanten.size,
  }
}

/**
 * Korte samenvatting voor het mail-onderwerp ("Dekkingsrapport week 29: ...").
 * Zonder tekorten: "alle diensten gepland". Met tekorten: "3 klanten met
 * tekorten" (enkelvoud: "1 klant met een tekort"). Overbezetting telt alleen
 * mee als de TE_VEEL-optie aanstaat.
 */
export function bouwDekkingSamenvatting(totalen: DekkingTotalen, metTeVeel: boolean): string {
  const delen: string[] = []
  if (totalen.klantenMetTekort > 0) {
    delen.push(
      totalen.klantenMetTekort === 1
        ? '1 klant met een tekort'
        : `${totalen.klantenMetTekort} klanten met tekorten`,
    )
  }
  if (metTeVeel && totalen.klantenTeVeel > 0) {
    delen.push(
      totalen.klantenTeVeel === 1
        ? '1 klant overbezet'
        : `${totalen.klantenTeVeel} klanten overbezet`,
    )
  }
  if (delen.length === 0) return 'alle diensten gepland'
  return delen.join(', ')
}

export interface DekkingRapportInput {
  doelweek: DekkingDoelweek
  roosterTekorten: DekkingRoosterRegel[]
  onderhoudAfwijkingen: DekkingOnderhoudRegel[]
  /** Alleen gerenderd wanneer `metTeVeel` aanstaat. */
  roosterTeVeel: DekkingRoosterRegel[]
  metTeVeel: boolean
  /**
   * batch-49 in-te-plannen (FO-398 §8) — dagregels in de doelweek ZONDER
   * uitvoerder. Apart benoemd, nooit verrekend met de tekorten: een tekort
   * vraagt om bijplannen, een onbemande dienst vraagt om toewijzen. Ze op één
   * hoop gooien maakt beide onbruikbaar. 0 = de regel wordt weggelaten.
   */
  onbemandeDiensten: number
  /** Volledige URL naar het dekkingsoverzicht op de doelweek. */
  deeplink: string
}

function sorteerOpKlant<T extends { clientNaam: string }>(regels: T[]): T[] {
  return [...regels].sort((a, b) => a.clientNaam.localeCompare(b.clientNaam, 'nl'))
}

/**
 * FO-348 §9 — de dekkingstelling is per batch-40 verbeterd (per rooster en per
 * dienst-slot), dus de cijfers in de eerstvolgende mails wijken af van eerdere
 * rapporten. Zonder deze regel leest dat als een fout in het rapport.
 *
 * Grens = de laatste doelweek-maandag die de notitie meekrijgt. Bewust aan de
 * ruime kant (1 sept ~ zes weken na de verwachte batch-40-deploy rond 20 juli):
 * de constante hangt aan de wandklok, niet aan het deploy-moment, dus een paar
 * weken uitstel mag de toelichting niet wegnemen. Te vroeg = de lezer denkt dat
 * het rapport stuk is; te laat = een overbodige regel. Daarna valt hij vanzelf
 * weg - opruimen van deze constante mag, hoeft niet.
 */
const TELLING_WIJZIGING_NOTITIE_TOT = Date.UTC(2026, 8, 1) // 1 september 2026

/** Toont de FO-348-voetnoot zolang de doelweek binnen het toelichtingsvenster valt. */
function toonTellingWijzigingNotitie(doelweekMaandag: Date): boolean {
  return doelweekMaandag.getTime() < TELLING_WIJZIGING_NOTITIE_TOT
}

/**
 * Rendert de volledige rapporttekst voor de `dekking_volgende_week`-placeholder
 * (plain text; de mail-engine zet regeleindes om naar HTML). Kopregel met
 * weeknummer + datumbereik + totalen, daarna per bron een sectie gesorteerd op
 * klant, afgesloten met de doorklik-link. Nul tekorten -> de
 * eenregel-bevestiging (FO-148: geen mail is niet hetzelfde als geen tekorten).
 */
export function bouwDekkingRapportTekst(input: DekkingRapportInput): string {
  const { doelweek, metTeVeel, deeplink } = input
  const totalen = telDekkingTotalen(
    input.roosterTekorten,
    input.onderhoudAfwijkingen,
    input.roosterTeVeel,
  )
  const kop = `Week ${doelweek.weekNummer} (${formatDekkingDatumBereik(doelweek.maandag, doelweek.zondag)})`
  const toonTeVeel = metTeVeel && input.roosterTeVeel.length > 0
  const regels: string[] = []

  if (totalen.klantenMetTekort === 0) {
    regels.push(`${kop}: alle diensten voor volgende week zijn gepland.`)
  } else {
    const dienstenDeel =
      totalen.ontbrekendeDiensten === 1
        ? '1 ontbrekende dienst'
        : `${totalen.ontbrekendeDiensten} ontbrekende diensten`
    const klantenDeel =
      totalen.klantenMetTekort === 1
        ? '1 klant met een tekort'
        : `${totalen.klantenMetTekort} klanten met tekorten`
    regels.push(`${kop}: ${klantenDeel}, ${dienstenDeel}.`)
  }

  if (input.roosterTekorten.length > 0) {
    regels.push('')
    regels.push('Tekorten in de roosterdekking:')
    for (const r of sorteerOpKlant(input.roosterTekorten)) {
      const tekort = r.verwacht - r.gepland
      regels.push(
        `- ${r.clientNaam} (rooster ${r.roosterNaam}): ${r.gepland} van ${r.verwacht} diensten gepland, ${tekort} ontbrekend`,
      )
    }
  }

  // batch-49 in-te-plannen (FO-398 §8) — eigen regel, boven de onderhoud-blok:
  // dit is werk dat KLAAR staat en alleen nog een uitvoerder mist. De planner
  // lost het op in het vak "In te plannen" bovenaan het planbord.
  if (input.onbemandeDiensten > 0) {
    regels.push('')
    regels.push(
      input.onbemandeDiensten === 1
        ? '1 dienst zonder uitvoerder (staat in "In te plannen" op het planbord).'
        : `${input.onbemandeDiensten} diensten zonder uitvoerder (staan in "In te plannen" op het planbord).`,
    )
  }

  if (input.onderhoudAfwijkingen.length > 0) {
    regels.push('')
    regels.push('Afwijkingen onderhoudscontrole:')
    for (const o of sorteerOpKlant(input.onderhoudAfwijkingen)) {
      regels.push(`- ${o.clientNaam}: ${o.statusLabel} (${o.detail})`)
    }
  }

  if (toonTeVeel) {
    regels.push('')
    // FO-348 — "te veel" is sinds batch-40 preciezer: er is gewerkt op dagen die
    // het rooster niet draait. De oude telling ("meer dagen dan verwacht") ging
    // ook af op een klant mét een gat die er één zaterdagklus naast had.
    regels.push('Overbezetting (gewerkt buiten het rooster):')
    for (const r of sorteerOpKlant(input.roosterTeVeel)) {
      const dagen = r.extraDagen === 1 ? '1 dag' : `${r.extraDagen} dagen`
      regels.push(
        `- ${r.clientNaam} (rooster ${r.roosterNaam}): ${r.gepland} van ${r.verwacht} diensten gedekt, ${dagen} buiten het rooster`,
      )
    }
  }

  // FO-348 §9 — tijdelijke toelichting bij de eerste rapporten na de nieuwe
  // telling. Vóór de deeplink: die blijft de afsluiter/call-to-action.
  if (toonTellingWijzigingNotitie(doelweek.maandag)) {
    regels.push('')
    regels.push(
      'De dekkingstelling is verbeterd (per rooster en per dienst); cijfers kunnen afwijken van eerdere rapporten.',
    )
  }

  regels.push('')
  regels.push(`Bekijk het dekkingsoverzicht: ${deeplink}`)
  return regels.join('\n')
}
